import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useState } from "react";
import Input from "components/shared/inputs/input";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import { useCreateVersionMutation, useUpdateVersionMutation } from "store/api";
import type { DatasourceMutationRequest } from "./TvaPopup";

type EditableVersionRow = {
  id?: number | string;
  key?: number | string;
  version?: string;
  fr?: string;
  en?: string;
};

type ChangelogPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  mode?: "create" | "edit";
  row?: EditableVersionRow | null;
  onSaved?: () => Promise<void> | void;
};

const ChangelogPopup: React.FC<ChangelogPopupProps> = ({
  isOpen,
  onClose,
  request,
  mode = "edit",
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [createVersion] = useCreateVersionMutation();
  const [updateVersion] = useUpdateVersionMutation();
  const isEditMode = mode === "edit";

  const { control, reset, getValues, watch } = useCustomForm<{
    version: string;
    fr: string;
    en: string;
  }>({
    defaultValues: {
      version: "",
      fr: "",
      en: "",
    },
  });

  useEffect(() => {
    setIsSaving(false);
    reset({
      version: isOpen ? String(row?.version ?? "") : "",
      fr: isOpen ? String(row?.fr ?? "") : "",
      en: isOpen ? String(row?.en ?? "") : "",
    });
  }, [isOpen, reset, row?.version, row?.fr, row?.en]);

  const versionValue = watch("version");
  const frValue = watch("fr");
  const enValue = watch("en");
  const canSave =
    String(versionValue ?? "").trim().length > 0 &&
    String(frValue ?? "").trim().length > 0 &&
    String(enValue ?? "").trim().length > 0;

  const handleSave = async () => {
    if (!request) return;
    const rowId = row?.id ?? row?.key;
    if (isEditMode && rowId == null) return;

    const { version, fr, en } = getValues();
    const baseUrl = request.url.replace(/\/+$/, "").replace(/\/[^/]+$/, "");
    const url = isEditMode ? `${baseUrl}/${rowId}` : baseUrl;

    const payload: Record<string, unknown> = {
      version: String(version ?? ""),
      fr: String(fr ?? ""),
      en: String(en ?? ""),
      ...(isEditMode ? { id: rowId } : {}),
    };

    setIsSaving(true);
    try {
      if (isEditMode) {
        await updateVersion({ url, data: payload }).unwrap();
      } else {
        await createVersion({ url, data: payload }).unwrap();
      }

      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={isEditMode ? "Modifier version" : "Nouvelle version"}
      onClose={onClose}
      portal
      fixSize="medium"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving || !canSave}>
              {t(isEditMode ? "Update" : "Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            <Input dataLength="autofit" label={t("Version")} name="version" control={control} type="text" size="large" />
            <Input dataLength="autofit" label={t("Description fr")} name="fr" control={control} type="textarea" size="large" />
            <Input dataLength="autofit" label={t("Description en")} name="en" control={control} type="textarea" size="large" />
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default ChangelogPopup;
